/**
 * Overview.tsx — the Manager Metrics Dashboard's lead page (MD-F07..F16, plus the two figures the
 * dashboard leads on).
 *
 * WHAT LEADS, AND WHY IT CHANGED. Until this pass the page was one real MetricCard and six
 * AwaitingDataCards: everything a manager saw first was a figure the platform cannot produce, which
 * reads as a broken screen rather than an honest one. Stream C's standing decision put ADOPTION BY
 * DEPARTMENT and COMPETENCY MOVEMENT FROM BASELINE at the head of it — both real, both from data the
 * platform already records — and took the money headline off the dashboard entirely. The
 * activity-dependent measures still appear, still honestly, but they appear LAST: they are the smaller
 * part of what this page knows, and putting them first misrepresented the platform.
 *
 * EVERY FIGURE ON THIS PAGE NOW HAS A SOURCE. It used to have exactly one — the aggregate
 * adoption count — with the other six rendered as a fixed "awaiting activity tracking"
 * placeholder, because nothing in the platform recorded a view. `db/activity.ts` records them now (one
 * per person per tool per UTC day, ADR-007) and `db/platformActivity.ts` computes reach, active people,
 * stickiness, return rate, dormancy and adoption conversion from that log.
 *
 * A FIGURE CAN STILL BE ABSENT, AND FOR FIVE DIFFERENT REASONS: recording has not started; it started
 * more recently than the chosen period reaches back; the comparison needs history older than the
 * 90-day retention window (AE-F18); no headcount has been recorded for this scope (MD-F04); or too few
 * distinct people were counted to report one without identifying them (MD-NFR05). Each card says WHICH,
 * because they imply different next actions — wait for a deploy, wait for time to pass, choose a
 * shorter period, go and record a headcount, wait for more people to be counted — and a reader who
 * cannot tell them apart reads every blank card as a bug. There is NO BACKFILL, so the date recording began is
 * stated beneath the cards rather than left for someone to infer from a suspiciously low number.
 *
 * WHAT THIS PAGE MUST NEVER SHOW (MD-NFR10, NT §8): no saves, no likes, no ratings, no badges and no
 * followers; and no dormant-person LIST — dormancy is a count, and nobody is contacted about it.
 *
 * PRIVACY, AND THE ONE THING NOT TO ADD. Every figure here is an aggregate that names nobody, and each
 * people-count is suppressed below min_cohort_for_aggregate by the engine (MD-NFR04/05). Two additions
 * would break that quietly, and both look harmless:
 *   - a TOTAL or a percentage-of-total on the department table. `overview.adoptionsInWindow` is on this
 *     same payload, and a reader who could subtract the published department rows from a people-total
 *     would recover the suppressed ones. The published total is a count of tool-person ADOPTIONS, not
 *     of people, which is exactly why it cannot be subtracted from — so do not relabel it either.
 *   - a denominator under the movement figures. movedUp + movedDown + unchanged is the number of
 *     declarations, and /learning's per-level cells (MD-F06) are suppressed against precisely that
 *     total. "9 of 18 have moved up" would hand a withheld cell back from the other page.
 * A withheld figure always renders its own reason — never a zero, and never a blank cell.
 *
 * ONE RESIDUAL THIS VIEW CANNOT CLOSE, recorded so nobody reads the restraint above as a guarantee.
 * The Tooling page publishes `creatorsConsumers.consumers`, which IS a platform-wide count of PEOPLE
 * who adopted; the department breakdown here is a partition of very nearly the same population.
 * Measured on a mutated database with two governed departments and one of them below the floor:
 * consumers 10, the one published cell 6, residual 4 — exactly the withheld cell. Two published
 * figures from two pages of one API, and the suppression is undone by a subtraction. It cannot be
 * fixed here: withholding a figure in the view while the API still answers with it is decorative
 * (privacy in this codebase lives in the data layer). It belongs to whoever owns
 * getCreatorsConsumersWith and getAdoptionByDepartmentWith in db/managerMetrics.ts.
 */
import { useLoaderData } from 'react-router';
import type { ActivePeopleSeries, DepartmentAdoption, OverviewResponse, PageState } from './api';
import { SignedOut } from '../admin/shared';
import { NotPermitted } from '../admin/overlay';
import { AsOfNote, RateCard, periodLabel } from './shared';
import { BarChart, LineChart } from './charts';

/** The day view recording began, in the reader's locale. Never a raw ISO timestamp on a manager's page. */
function formatSince(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
}

/**
 * One department's row. The suppressed and unavailable cases each render the engine's own sentence:
 * this marker's reason set includes 'too-few', which AwaitingDataCard does not know about and would
 * answer with copy about a missing activity log.
 */
function DepartmentRow({ department }: { department: DepartmentAdoption }) {
  const { adopters, share } = department;
  return (
    <tr>
      <td>{department.label}</td>
      <td className="tb-num">{adopters.available ? adopters.count.toLocaleString() : 'Too few'}</td>
      <td>
        <strong>{share.available ? `${share.pct}%` : '—'}</strong>
        <p className="tb-item-meta">
          {share.available
            ? `Of a recorded headcount of ${share.headcount.toLocaleString()}.`
            : share.detail}
        </p>
      </td>
    </tr>
  );
}

/**
 * MD-F17/F02 — which departments have actually turned up. Every live governed department gets a row
 * whether anyone in it adopted or not: the axis is governed data, so a missing row would read as
 * missing data rather than the real nought it is, and a department where nobody adopted is itself the
 * finding a manager came for.
 *
 * Rendered in the server's LABEL order and never re-sorted. Ordering by size would also order the
 * suppressed rows, and the position of two withheld figures tells the reader which is larger.
 */
function AdoptionByDepartment({
  departments,
  window,
}: {
  departments: DepartmentAdoption[];
  window: string;
}) {
  if (departments.length === 0) {
    return (
      <p className="tb-empty">
        No department is governed yet, so adoption cannot be broken down by one. The platform owner
        adds departments in the administration surface.
      </p>
    );
  }
  return (
    <>
      {/* Chart above the table (MD-NFR07: the table below is the text equivalent, and it carries the
          share column the chart omits). Suppressed rows pass value:null so BarChart draws "Too few"
          with no bar — a zero bar would read as "nobody adopted", handing back the withheld count.
          Row order is the server's label order, unchanged: BarChart never re-sorts, so the position
          of two suppressed rows still says nothing about which is larger. */}
      <BarChart
        unit="people who adopted"
        data={departments.map((d) => ({
          label: d.label,
          value: d.adopters.available ? d.adopters.count : null,
        }))}
      />
      <table className="tb-dash-table">
        <caption className="tb-muted" style={{ captionSide: 'bottom', textAlign: 'left' }}>
          Distinct people in each department who adopted a colleague&rsquo;s tool, {window}, as a
          share of the headcount recorded for that department. Departments are listed
          alphabetically, never by size.
        </caption>
        <thead>
          <tr>
            <th scope="col">Department</th>
            <th scope="col" className="tb-num">
              People who adopted
            </th>
            <th scope="col">Share of recorded headcount</th>
          </tr>
        </thead>
        <tbody>
          {departments.map((d) => (
            <DepartmentRow key={d.departmentId} department={d} />
          ))}
        </tbody>
      </table>
      <p className="tb-muted" style={{ maxWidth: '68ch' }}>
        A department is a person&rsquo;s live directory department, so this counts who USED a tool,
        as a share of a headcount the platform owner records (never derived from account numbers).
      </p>
    </>
  );
}

/**
 * The active-people line graph (MD-F09), the shape that replaced the three "active today / this week /
 * this month" cards. One point per UTC day across the selected period, so it visibly changes shape as the
 * manager moves the 7 / 30 / 180 filter. A day suppressed below the cohort floor BREAKS the line rather
 * than plotting a zero (LineChart/coordRuns), so a small team's quiet days read as withheld, not as nil.
 */
function ActivePeopleGraph({ series, window }: { series: ActivePeopleSeries; window: string }) {
  return (
    <LineChart
      unit="Active people"
      data={series.points.map((p) => ({ date: p.day, value: p.count }))}
      caption={
        series.trackingSince != null
          ? `Distinct people active each day, ${window}. The shape follows the period selected above; recording began ${formatSince(series.trackingSince)}, and earlier days are not shown.`
          : 'No activity has been recorded yet — the graph fills in as people use the platform.'
      }
    />
  );
}

export function Overview() {
  const data = useLoaderData() as PageState<OverviewResponse>;
  if (data.state === 'signedOut')
    return <SignedOut title="Management dashboard" returnTo="/dashboard/legacy" />;
  if (data.state === 'forbidden') return <NotPermitted what="Management dashboard" />;

  const { overview, activePeopleSeries, adoptionByDepartment, filter, asOf } = data.data;
  const window = periodLabel(filter.windowDays);

  return (
    <div>
      <h2>Adoption &amp; reuse</h2>
      {/* MD-F24: one line stating the period every figure below is measured over. */}
      <p className="tb-filter-live" aria-live="polite">
        Showing the {window}
        {filter.teamId != null
          ? ', scoped to your team'
          : filter.departmentId != null
            ? ', filtered by department'
            : ', all departments'}
        .
      </p>
      <AsOfNote asOf={asOf} />

      <h3>Adoption by department</h3>
      <AdoptionByDepartment departments={adoptionByDepartment.departments} window={window} />

      <h3>Active people</h3>
      <ActivePeopleGraph series={activePeopleSeries} window={window} />

      <h3>Platform health</h3>
      <div className="tb-dash-grid">
        {/* MD-F07 — the denominator is the RECORDED headcount for this scope, never a count of accounts.
            Under a team scope there is no per-team headcount, so this reports its own honest absence. */}
        <RateCard
          label="Reach (share of workforce)"
          figure={overview.reach}
          unit={(n, d) => `${n.toLocaleString()} of ${d.toLocaleString()} people`}
          note={`Active at least once ${window}.`}
        />
        {/* MD-F10 — daily active over monthly active; both windows are fixed by the requirement. */}
        <RateCard
          label="Stickiness (daily / monthly)"
          figure={overview.stickiness}
          unit={(n, d) => `${n.toLocaleString()} active today of ${d.toLocaleString()} this month`}
          note="How much of the month's audience shows up on a given day."
        />
      </div>
    </div>
  );
}
